import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'
import { Capacitor } from '@capacitor/core'
import { router } from './routeTree'
import { FLAVOR } from './flavor/flavor'
import { getAccessToken, initStorage } from './auth/tokenStorage'
import { appForUrl } from './auth/stepupStore'
import { stepupFetch } from './auth/stepupFetch'
import { initLiquidGlass } from '@foroba/ui'
import { registerAppLifecycle } from './native/appLifecycle'
import { registerConsentCallbackHandler } from './native/bankConsent'
import { registerPushNotifications } from './native/pushNotifications'
import { registerKeyboardScrollReset } from './native/keyboardScroll'
import './i18n' // initialize i18next before any component renders
import '@foroba/ui/tokens.css'
import './embedded.css'
import './index.css'

// BR-14: per-app accent from @foroba/ui tokens ([data-app=…])
document.documentElement.dataset.app = FLAVOR.dataApp
// Plan AT: resolves html[data-glass] (WKWebView/iOS => frost tier) and the pointer light.
initLiquidGlass()

// ── Native fetch interceptor ──────────────────────────────────────────────────
//
// Injects Authorization: Bearer on every /api/* call so embedded Pazuru/Tsumi
// pages work without modifying their source.
//
// In native Capacitor builds with VITE_API_BASE_URL set, also rewrites
// relative /api/* paths to absolute versioned URLs (ADR 0001 /api/v1/*).
//
// Route mapping mirrors Vite dev proxy and nginx api-v1.conf:
//   /api/v1/foroba  → Foroba backend (strip prefix)
//   /api/v1/pazuru  → Pazuru backend (strip prefix)
//   /api/v1/tsumi   → Tsumi backend (strip prefix)

const _apiBase: string = import.meta.env.VITE_API_BASE_URL ?? ''
const _isNative = Capacitor.isNativePlatform()

const _nativeFetch = globalThis.fetch.bind(globalThis)
globalThis.fetch = (rawInput: RequestInfo | URL, init?: RequestInit) => {
  // Embedded Foroba pages call the web shell's `/foroba-api/*` alias, which only exists on
  // the Foroba vhost. Map it onto the versioned path every pfh-mobile transport serves.
  const input: RequestInfo | URL =
    typeof rawInput === 'string' && rawInput.startsWith('/foroba-api/')
      ? rawInput.replace('/foroba-api/', '/api/v1/foroba/')
      : rawInput
  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : (input as Request).url

  if (url.startsWith('/api/') || url.startsWith('/jeli/')) {
    const headers = new Headers(init?.headers)

    // Bearer token injection for all /api/* requests
    const token = getAccessToken()
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    // Native URL rewriting: relative /api/* → absolute versioned URL
    let resolvedInput: RequestInfo | URL = input
    if (_isNative && _apiBase) {
      let absUrl: string
      if (url.startsWith('/api/v1/')) {
        absUrl = _apiBase + url
      } else if (url.startsWith('/api/foroba/')) {
        absUrl = _apiBase + url.replace('/api/foroba/', '/api/v1/foroba/')
      } else if (appForUrl(url) === 'tsumi') {
        absUrl = _apiBase + '/api/v1/tsumi' + url
      } else if (appForUrl(url) === 'pazuru') {
        absUrl = _apiBase + '/api/v1/pazuru' + url
      } else {
        absUrl = _apiBase + url
      }
      resolvedInput = absUrl
    }

    return stepupFetch(_nativeFetch, url, resolvedInput, init, headers)
  }

  return _nativeFetch(input, init)
}

// ── App startup ───────────────────────────────────────────────────────────────

async function bootstrap() {
  // Load tokens from Keychain (iOS) / localStorage (web) before first render
  await initStorage()
  // Register foreground token-refresh listener (no-op on web)
  registerAppLifecycle()
  // Register PSD2 consent callback deep-link handler (no-op on web)
  registerConsentCallbackHandler()
  // Register for APNs push notifications (token capture groundwork; delivery needs ADP)
  void registerPushNotifications()
  registerKeyboardScrollReset()

  const rootEl = document.getElementById('root')
  if (!rootEl) throw new Error('Root element not found')

  createRoot(rootEl).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  )
}

void bootstrap()
